// REPORTS & ANALYTICS API - Frontend API integration for timesheet reports and analytics.
// Phase 7: Reports & Analytics

import axios from 'axios'
import {setupAxios} from '../app/modules/auth/core/AuthHelpers'

// const API_BASE_URL = 'http://localhost:8000/api/v1'
const API_BASE_URL = process.env.REACT_APP_API_KEY

// =================== TEAM UTILIZATION REPORT ===================

export const getTeamUtilizationReport = async (filters: {
  managerId: string
  startDate: string
  endDate: string
  userId?: string
  includeLeave?: boolean
}) => {
  const {token} = setupAxios()
  const params = new URLSearchParams()
  params.append('managerId', filters.managerId)
  params.append('startDate', filters.startDate)
  params.append('endDate', filters.endDate)
  if (filters.userId) params.append('userId', filters.userId)
  if (filters.includeLeave !== undefined)
    params.append('includeLeave', String(filters.includeLeave))

  return axios.get(`${API_BASE_URL}/timesheet/reports/team-utilization?${params.toString()}`, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

// =================== PROJECT ANALYSIS REPORT ===================

export const getProjectAnalysisReport = async (filters: {
  startDate: string
  endDate: string
  projectCodeId?: string
  projectType?: string
  managerId?: string
}) => {
  const {token} = setupAxios()
  const params = new URLSearchParams()
  params.append('startDate', filters.startDate)
  params.append('endDate', filters.endDate)
  if (filters.projectCodeId) params.append('projectCodeId', filters.projectCodeId)
  if (filters.projectType) params.append('projectType', filters.projectType)
  if (filters.managerId) params.append('managerId', filters.managerId)

  return axios.get(
    `${API_BASE_URL}/timesheet/reports/project-analysis?${params.toString()}`,
    {
      headers: {Authorization: `Bearer ${token}`},
    }
  )
}

// =================== EMPLOYEE PRODUCTIVITY REPORT ===================

export const getEmployeeProductivityReport = async (filters: {
  userId: string
  startDate: string
  endDate: string
}) => {
  const {token} = setupAxios()
  const params = new URLSearchParams()
  params.append('userId', filters.userId)
  params.append('startDate', filters.startDate)
  params.append('endDate', filters.endDate)

  return axios.get(
    `${API_BASE_URL}/timesheet/reports/employee-productivity?${params.toString()}`,
    {
      headers: {Authorization: `Bearer ${token}`},
    }
  )
}

// =================== APPROVAL ANALYTICS REPORT ===================

export const getApprovalAnalyticsReport = async (filters: {
  managerId: string
  startDate: string
  endDate: string
}) => {
  const {token} = setupAxios()
  const params = new URLSearchParams()
  params.append('managerId', filters.managerId)
  params.append('startDate', filters.startDate)
  params.append('endDate', filters.endDate)

  return axios.get(
    `${API_BASE_URL}/timesheet/reports/approval-analytics?${params.toString()}`,
    {
      headers: {Authorization: `Bearer ${token}`},
    }
  )
}

// =================== EXPORT REPORT ===================

export const exportReport = async (filters: {
  reportType: 'team-utilization' | 'project-analysis' | 'employee-productivity' | 'approval-analytics'
  format: 'csv' | 'xlsx'
  startDate: string
  endDate: string
  managerId?: string
  userId?: string
  projectCodeId?: string
  projectType?: string
  includeLeave?: boolean
}): Promise<Blob> => {
  const {token} = setupAxios()
  const params = new URLSearchParams()
  params.append('reportType', filters.reportType)
  params.append('format', filters.format)
  params.append('startDate', filters.startDate)
  params.append('endDate', filters.endDate)
  if (filters.managerId) params.append('managerId', filters.managerId)
  if (filters.userId) params.append('userId', filters.userId)
  if (filters.projectCodeId) params.append('projectCodeId', filters.projectCodeId)
  if (filters.projectType) params.append('projectType', filters.projectType)
  if (filters.includeLeave !== undefined)
    params.append('includeLeave', String(filters.includeLeave))

  const response = await axios.get(
    `${API_BASE_URL}/timesheet/reports/export?${params.toString()}`,
    {
      headers: {Authorization: `Bearer ${token}`},
      responseType: 'blob', // Important for file download
    }
  )

  return response.data
}

// =================== TYPE DEFINITIONS ===================

export interface TeamUtilizationSummary {
  totalBillableHours: number
  totalActualHours: number
  utilizationRate: number
  overtimeHours: number
  leaveHours: number
  holidayHours: number
  teamSize: number
  workingDays: number
}

export interface EmployeeUtilization {
  userId: string
  employeeCode: string
  name: string
  billableHours: number
  actualHours: number
  utilizationRate: number
  overtimeHours: number
  entriesCount: number
  approvedCount: number
  rejectedCount: number
}

export interface DailyTrend {
  date: string
  billableHours: number
  actualHours: number
  employeeCount: number
}

export interface ProjectSummary {
  totalProjects: number
  totalHours: number
  totalBillableHours: number
  averageHoursPerProject: number
  internalProjects: number
  realProjects: number
}

export interface ProjectBreakdown {
  projectId: string
  projectCode: string
  projectName: string
  projectType: string
  billableHours: number
  actualHours: number
  employeeCount: number
  entriesCount: number
  utilizationRate: number
}

export interface EmployeeProductivityMetrics {
  totalHours: number
  billableHours: number
  billableRate: number
  overtimeHours: number
  approvalRate: number
  rejectionCount: number
  uniqueProjects: number
  averageHoursPerDay: number
}

